import React, { useCallback, useState } from 'react';
import { Modal, Spin, Typography, Space, Alert } from 'antd';
import { LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import { useGetStripeConfigQuery, useCreateCheckoutSessionMutation, useCreateStampCheckoutMutation } from '../../services/stripeApi';

const { Text, Title } = Typography;

type CheckoutType = 'subscription' | 'stamp_package';

interface EmbeddedCheckoutModalProps {
  open: boolean;
  planName: string;
  planDisplayName?: string;
  onClose: () => void;
  onComplete?: () => void;
  checkoutType?: CheckoutType;
  packageName?: string; // For stamp packages
}

const EmbeddedCheckoutModal: React.FC<EmbeddedCheckoutModalProps> = ({
  open,
  planName,
  planDisplayName,
  onClose,
  onComplete,
  checkoutType = 'subscription',
  packageName,
}) => {
  const { data: stripeConfig, isLoading: loadingConfig } = useGetStripeConfigQuery();
  const [createCheckoutSession] = useCreateCheckoutSessionMutation();
  const [createStampCheckout] = useCreateStampCheckoutMutation();
  const [error, setError] = useState<string | null>(null);

  const isStampPackage = checkoutType === 'stamp_package';

  // Initialize Stripe
  const stripePromise = React.useMemo(() => {
    if (stripeConfig?.publishableKey) {
      return loadStripe(stripeConfig.publishableKey);
    }
    return null;
  }, [stripeConfig?.publishableKey]);

  // Fetch client secret when modal opens
  const fetchClientSecret = useCallback(async () => {
    try {
      setError(null);
      if (isStampPackage && packageName) {
        const result = await createStampCheckout({ packageName }).unwrap();
        return result.clientSecret;
      } else {
        const result = await createCheckoutSession({ planName }).unwrap();
        return result.clientSecret;
      }
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(err?.data?.message || 'Error al crear la sesión de pago');
      throw err;
    }
  }, [createCheckoutSession, createStampCheckout, planName, packageName, isStampPackage]);

  // Handle checkout completion
  const onCheckoutComplete = useCallback(() => {
    onComplete?.();
    onClose();
  }, [onComplete, onClose]);

  if (!open) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={600}
      centered
      destroyOnClose
      styles={{
        body: { padding: 0 },
        content: { borderRadius: 16, overflow: 'hidden' },
      }}
      title={
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border-color)' }}>
          <Title level={4} style={{ margin: 0 }}>
            {isStampPackage
              ? `Comprar Paquete ${planDisplayName || packageName}`
              : `Suscripción al Plan ${planDisplayName || planName}`}
          </Title>
          <Text type="secondary">Completa tu información de pago</Text>
        </div>
      }
    >
      {/* Loading state */}
      {loadingConfig && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Cargando formulario de pago...</Text>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div style={{ padding: 24 }}>
          <Alert
            type="error"
            message="Error"
            description={error}
            showIcon
          />
        </div>
      )}

      {/* Embedded Checkout */}
      {stripePromise && stripeConfig && !loadingConfig && (
        <div style={{ minHeight: 400 }}>
          <EmbeddedCheckoutProvider
            stripe={stripePromise}
            options={{
              fetchClientSecret,
              onComplete: onCheckoutComplete,
            }}
          >
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>
      )}

      {/* Security Notice */}
      <div
        style={{
          background: 'linear-gradient(135deg, #f6f9fc 0%, #eef2f7 100%)',
          borderTop: '1px solid #e6ebf1',
          padding: '12px 24px',
        }}
      >
        <Space align="center" style={{ width: '100%', justifyContent: 'center' }}>
          <LockOutlined style={{ fontSize: 16, color: '#635bff' }} />
          <div style={{ textAlign: 'center' }}>
            <Text style={{ color: '#32325d', fontSize: 12 }}>
              Pagos seguros con{' '}
              <Text strong style={{ color: '#635bff' }}>Stripe</Text>
            </Text>
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
              <SafetyCertificateOutlined style={{ marginRight: 4 }} />
              PCI DSS Nivel 1
            </Text>
          </div>
        </Space>
      </div>

      {/* Stamp package info */}
      {isStampPackage && (
        <div style={{ padding: '12px 24px', background: 'var(--bg-warning-light)', textAlign: 'center' }}>
          <Text style={{ color: '#d48806' }}>
            Los timbres comprados <strong>no expiran</strong> y se acumulan con los de tu plan.
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default EmbeddedCheckoutModal;
